import { ExpiryTimeGranularity, ExpiryTimeGranularityType } from '@x-guard/xgac-types/xgac';
import { Translate } from 'react-admin';
import { roundDaysToExpiryTimeGranularity, TimeGranularityLabel } from './timeGranularityUtils';

export type ExpiryStatus = 'valid' | 'expiring' | 'expired';

export const DEFAULT_REMINDER_PERIODS: ExpiryTimeGranularity[] = [
  { type: ExpiryTimeGranularityType.Day, value: 1 },
  { type: ExpiryTimeGranularityType.Week, value: 1 },
  { type: ExpiryTimeGranularityType.Month, value: 1 },
];

const EXPIRY_REMINDER_DAYS = 7;

const UNTIL_EXPIRY_KEYS: Record<ExpiryTimeGranularityType, string> = {
  [ExpiryTimeGranularityType.Day]:
    'resources.certification-categories.text.days_until_expiry',
  [ExpiryTimeGranularityType.Week]:
    'resources.certification-categories.text.weeks_until_expiry',
  [ExpiryTimeGranularityType.Month]:
    'resources.certification-categories.text.months_until_expiry',
  [ExpiryTimeGranularityType.Year]:
    'resources.certification-categories.text.years_until_expiry',
};

export const daysUntilNextPostExpiryWeeklyReminder = (
  daysUntilExpiry: number,
): number => {

  if (daysUntilExpiry >= 0) {

    return daysUntilExpiry;

  }

  const daysPastExpiry = Math.abs(daysUntilExpiry);

  return (
    EXPIRY_REMINDER_DAYS
    - (daysPastExpiry % EXPIRY_REMINDER_DAYS)
  ) % EXPIRY_REMINDER_DAYS;

};

export const calculateDaysUntilDate = (date: string) => Math.floor((new Date(date).setUTCHours(23, 59, 59, 999) - Date.now()) / (1000 * 60 * 60 * 24));

export const isCertificationOrItemExpiring = (
  expiryDate: string | undefined,
  largestReminderPeriodInDays: number,
): boolean => {

  if (!expiryDate) return false;
  return calculateDaysUntilDate(expiryDate) <= largestReminderPeriodInDays;

};

export const certificationExpiryChipColor = (
  days: number,
  largestReminderPeriodInDays: number,
): 'primary' | 'warning' | 'error' => {

  if (days > largestReminderPeriodInDays) return 'primary';
  if (days <= 0) return 'error';
  return 'warning';

};

export const getCertificationExpiryText = (
  days: number,
  translate: Translate,
  includeUntilExpiry = true,
) => {

  if (days === 0) {

    return translate('resources.certification-categories.text.expires_today');

  }

  const absoluteDays = Math.abs(days);
  const granularity = roundDaysToExpiryTimeGranularity(absoluteDays);
  const periodLabel = TimeGranularityLabel(granularity, translate);

  if (days < 0) {

    if (absoluteDays === 1) {

      return translate('resources.certification-categories.text.already_expired_for', {
        smart_count: 1,
      });

    }

    return translate(
      'resources.certification-categories.text.already_expired_for_period',
      { period: periodLabel },
    );

  }

  if (!includeUntilExpiry) {

    return periodLabel;

  }

  const untilExpiry = translate(UNTIL_EXPIRY_KEYS[granularity.type], {
    smart_count: granularity.value,
  });

  return granularity.approximate ? `≈ ${untilExpiry}` : untilExpiry;

};

export const getExpiryStatus = (
  expiryDate: string,
  largestReminderPeriodInDays: number,
): 'valid' | 'expiring' | 'expired' => {

  if (calculateDaysUntilDate(expiryDate) <= 0) {

    return 'expired';

  }

  if (isCertificationOrItemExpiring(expiryDate, largestReminderPeriodInDays)) {

    return 'expiring';

  }

  return 'valid';

};

export const hasActivePlannedRenewal = (
  plannedRenewalDate?: string | null,
): boolean => Boolean(plannedRenewalDate && calculateDaysUntilDate(plannedRenewalDate) >= 0);
